import {
  useAuthStore,
  getAccessToken as readAccessToken,
} from "../../stores/auth";

/**
 * Auth seam for the axios layer — the integration point between the interceptor/refresh flow
 * and the real auth store. Token read/write delegates to `useAuthStore` (via `getState()`, so
 * it works outside React); the logout-callback seam lets the app register a redirect without
 * this layer ever hard-navigating.
 *
 * The access token is in memory only (SEC-1). The refresh token is the httpOnly `dz_refresh`
 * cookie the browser manages; JS never reads it.
 */

type LogoutHandler = () => void;

let onLogout: LogoutHandler | null = null;

/** Read the current access token (used by the request interceptor). */
export function getAccessToken(): string | null {
  return readAccessToken();
}

/** Store a new access token (used by the refresh flow). */
export function setAccessToken(token: string | null): void {
  useAuthStore.getState().setAccessToken(token);
}

/** Clear the access token. */
export function clearAccessToken(): void {
  useAuthStore.getState().clearAuth();
}

/**
 * Registered by the app shell so the interceptor can signal "the session is gone" and the app
 * performs the redirect to login. This layer never navigates itself.
 */
export function registerLogoutHandler(handler: LogoutHandler): void {
  onLogout = handler;
}

/**
 * Called by the interceptor when refresh fails: clear the store (the state change) and signal
 * logout so the app can redirect (navigation stays the app's job).
 */
export function signalLogout(): void {
  useAuthStore.getState().clearAuth();
  onLogout?.();
}
